import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { FUNNEL_EVENT_TYPE, NEW_SESSION_FROM } from './types'
import type { FunnelEvent } from './types'
import {
  initSession,
  getSessionState,
  incrementSequence,
  setSessionState,
} from './session'
import { emitFunnelEvent } from './emit'

const extractPathname = (url: string): string => url.split('?')[0].split('#')[0]

export const useFunnelTransition = () => {
  const router = useRouter()

  useEffect(() => {
    if (!router.isReady) return

    if (initSession()) {
      const currentPath = extractPathname(router.asPath)
      emitTransition(NEW_SESSION_FROM, currentPath)
      setSessionState({ fromPage: currentPath })
    }

    const onRouteChangeStart = (url: string) => {
      const toPage = extractPathname(url)
      const { fromPage } = getSessionState()

      if (toPage === fromPage) return

      emitTransition(fromPage, toPage)
      setSessionState({ fromPage: toPage })
    }

    router.events.on('routeChangeStart', onRouteChangeStart)

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
    }
  }, [router])
}

function emitTransition(fromPage: string, toPage: string) {
  const { sessionId, lastEmitTimestamp } = getSessionState()
  const seq = incrementSequence()
  const now = Date.now()
  const elapsed = lastEmitTimestamp === 0 ? 0 : now - lastEmitTimestamp
  setSessionState({ lastEmitTimestamp: now })

  const event: FunnelEvent = {
    funnel_elapsed_since_last_ms: elapsed,
    funnel_event_type: FUNNEL_EVENT_TYPE,
    funnel_route_from_page: fromPage,
    funnel_route_to_page: toPage,
    funnel_route_transition_id: `${sessionId}-${seq}`,
    funnel_sequence_num: seq,
    funnel_session_id: sessionId,
  }

  emitFunnelEvent(event)
}
